import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import { slugify } from '../../../shared/utils/slugify';
import {
  createPartner,
  listPartnerContactCounts,
  subscribeToPartnerLocations,
  subscribeToPartners,
  updatePartner,
  updatePartnerLocation,
} from '../services/partnerRepository';
import type { Partner, PartnerLocation } from '../types';
import OrganizationSeasonsView from './OrganizationSeasonsView';
import '../styles/PartnerListPage.css';

type ViewMode = 'rows' | 'cards' | 'seasons';
type PartnerSignal = { label: string; tone: 'good' | 'warn' | 'muted' };

const STATE_OPTIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV',
  'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;
const STATE_CODES = new Set<string>([...STATE_OPTIONS, 'DC']);

const ORGANIZATION_TYPE_LABELS: Record<string, string> = {
  traditional_public: 'School',
  public_charter_school: 'Public charter',
  private_school: 'Private school',
  community_organization: 'Community organization',
  school_district: 'School district',
  park_district: 'Park district',
  other: 'Other',
};
const ORGANIZATION_TYPE_OPTIONS = Object.keys(ORGANIZATION_TYPE_LABELS);

function stateFromAddress(address?: string | null): string | null {
  if (!address) return null;
  const tokens = address.toUpperCase().match(/\b[A-Z]{2}\b/g) || [];
  return [...tokens].reverse().find((token) => STATE_CODES.has(token)) || null;
}

function organizationTypeLabel(value?: string | null): string {
  if (!value) return 'Unclassified';
  return ORGANIZATION_TYPE_LABELS[value]
    || value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function isStripePartner(partner: Partner): boolean {
  return String(partner.billingDefaults?.paymentMode || 'stripe').trim().toLowerCase() === 'stripe';
}

function StripePartnerBadge({ partner }: { partner: Partner }): React.ReactElement | null {
  if (!isStripePartner(partner)) return null;
  return <span className="partner-stripe-badge" title="Stripe school — parents pay directly" aria-label="Stripe school — parents pay directly">💰</span>;
}

function signalForPartner(partner: Partner, contactCount?: number): PartnerSignal {
  if (partner.active === false) return { label: 'Inactive', tone: 'muted' };
  if (contactCount === undefined) return { label: 'Checking contacts…', tone: 'muted' };
  if (contactCount === 0) return { label: 'Needs contact', tone: 'warn' };
  return { label: `${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`, tone: 'good' };
}

function addressWithState(address: string | null | undefined, nextState: string): string {
  const currentAddress = String(address || '').trim();
  if (!currentAddress) return nextState;
  const tokens = currentAddress.match(/\b[A-Z]{2}\b/g) || [];
  const currentState = [...tokens].reverse().find((token) => STATE_CODES.has(token));
  if (currentState) {
    const index = currentAddress.lastIndexOf(currentState);
    return `${currentAddress.slice(0, index)}${nextState}${currentAddress.slice(index + currentState.length)}`;
  }
  return `${currentAddress}, ${nextState}`;
}

export default function PartnerListPage(): React.ReactElement {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [locations, setLocations] = useState<PartnerLocation[]>([]);
  const [contactCounts, setContactCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'state' | 'type' | 'readiness'>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('seasons');
  const [editingStatesPartnerId, setEditingStatesPartnerId] = useState<string | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [organizationType, setOrganizationType] = useState('traditional_public');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToPartners(
      (items) => {
        setPartners(items);
        setLoading(false);
      },
      (nextError) => {
        setError(nextError.message);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    return subscribeToPartnerLocations(
      setLocations,
      (nextError) => setError(nextError.message)
    );
  }, []);

  useEffect(() => {
    if (partners.length === 0) {
      setContactCounts({});
      return;
    }
    let cancelled = false;
    listPartnerContactCounts(partners.map((partner) => partner.id))
      .then((counts) => {
        if (!cancelled) setContactCounts(counts);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [partners]);

  const locationsByPartner = useMemo(() => {
    const grouped = new Map<string, PartnerLocation[]>();
    locations.forEach((location) => {
      const existing = grouped.get(location.partnerId) || [];
      existing.push(location);
      grouped.set(location.partnerId, existing);
    });
    return grouped;
  }, [locations]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return partners.filter((partner) => {
      const partnerLocations = locationsByPartner.get(partner.id) || [];
      const states = partnerLocations.map((location) => stateFromAddress(location.address)).filter(Boolean);
      const matchesSearch = !needle || [
        partner.name,
        partner.slug,
        organizationTypeLabel(partner.organizationType),
        ...partnerLocations.map((location) => location.name),
        ...partnerLocations.map((location) => location.address),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
      const matchesState = stateFilter === 'all' || states.includes(stateFilter);
      const matchesType = typeFilter === 'all' || partner.organizationType === typeFilter;
      const contactCount = contactCounts[partner.id];
      const readiness = [Boolean(partner.organizationType), partnerLocations.length > 0, Boolean(contactCount)].filter(Boolean).length;
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'active' && partner.active !== false)
        || (statusFilter === 'inactive' && partner.active === false)
        || (statusFilter === 'needs_setup' && readiness < 3);
      return matchesSearch && matchesState && matchesType && matchesStatus;
    }).sort((a, b) => {
      const aLocations = locationsByPartner.get(a.id) || [];
      const bLocations = locationsByPartner.get(b.id) || [];
      if (sortBy === 'state') return String(stateFromAddress(aLocations[0]?.address) || '').localeCompare(String(stateFromAddress(bLocations[0]?.address) || ''));
      if (sortBy === 'type') return organizationTypeLabel(a.organizationType).localeCompare(organizationTypeLabel(b.organizationType));
      if (sortBy === 'readiness') {
        const aReady = [Boolean(a.organizationType), aLocations.length > 0, Boolean(contactCounts[a.id])].filter(Boolean).length;
        const bReady = [Boolean(b.organizationType), bLocations.length > 0, Boolean(contactCounts[b.id])].filter(Boolean).length;
        return aReady - bReady || a.name.localeCompare(b.name);
      }
      return a.name.localeCompare(b.name);
    });
  }, [contactCounts, locationsByPartner, partners, search, sortBy, stateFilter, statusFilter, typeFilter]);

  const handleTypeChange = async (partner: Partner, organizationType: string) => {
    setSavingCell(`type:${partner.id}`);
    setError(null);
    try {
      await updatePartner(partner.id, { organizationType }, currentUser?.uid);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to update partner type.');
    } finally {
      setSavingCell(null);
    }
  };

  const handleLocationStateChange = async (location: PartnerLocation, nextState: string) => {
    setSavingCell(`state:${location.id}`);
    setError(null);
    try {
      await updatePartnerLocation(
        location.id,
        { address: addressWithState(location.address, nextState) },
        currentUser?.uid
      );
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to update location state.');
    } finally {
      setSavingCell(null);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createPartner(
        {
          name: cleanName,
          slug: slugify(cleanName),
          timezone,
          organizationType,
          active: true,
          isPublic: false,
          branding: {},
          demographics: {},
          parentCommunicationPolicy: {},
          billingDefaults: {},
          legacySchoolIds: [],
          createdBy: currentUser?.uid || null,
          updatedBy: currentUser?.uid || null,
        },
        currentUser?.uid
      );
      navigate(`/app/admin/partners/${created.id}`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to create partner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="partner-list-page">
      <div className="partner-list-header">
        <div className="partner-list-title">
          <h1>Partners <span>{partners.length}</span></h1>
          <button
            type="button"
            className="partner-add-button"
            onClick={() => setShowCreate((value) => !value)}
            aria-label={showCreate ? 'Close new partner form' : 'Add partner'}
            title={showCreate ? 'Close new partner form' : 'Add partner'}
          >
            {showCreate ? '×' : '+'}
          </button>
        </div>
        <div className="partner-view-toggle" aria-label="Partner view">
          <button type="button" className={viewMode === 'rows' ? 'active' : ''} onClick={() => setViewMode('rows')}>
            Rows
          </button>
          <button type="button" className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')}>
            Cards
          </button>
          <button type="button" className={viewMode === 'seasons' ? 'active' : ''} onClick={() => setViewMode('seasons')}>
            Seasons
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="partner-list-error">
          {error}
        </div>
      )}

      {showCreate && viewMode !== 'seasons' && (
        <form onSubmit={handleCreate} className="partner-create-form">
          <h2 style={{ marginTop: 0 }}>New partner</h2>
          <div className="partner-create-fields">
            <label>
              <span>Partner name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            <label>
              <span>Organization type</span>
              <select value={organizationType} onChange={(event) => setOrganizationType(event.target.value)}>
                <option value="traditional_public">Traditional public</option>
                <option value="public_charter_school">Public charter</option>
                <option value="private_school">Private school</option>
                <option value="community_organization">Community organization</option>
                <option value="school_district">School district</option>
                <option value="park_district">Park district</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label>
              <span>Timezone</span>
              <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                <option value="America/Chicago">America/Chicago</option>
                <option value="America/New_York">America/New_York</option>
                <option value="America/Denver">America/Denver</option>
                <option value="America/Los_Angeles">America/Los_Angeles</option>
              </select>
            </label>
          </div>
          <div style={{ marginTop: 14 }}>
            <button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create partner'}</button>
          </div>
        </form>
      )}

      {viewMode !== 'seasons' && <div className="partner-filter-bar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search partners…"
          aria-label="Search partners"
        />
        <select value={stateFilter} onChange={(event) => setStateFilter(event.target.value)} aria-label="Filter by state">
          <option value="all">All states</option>
          {STATE_OPTIONS.map((state) => <option key={state} value={state}>{state}</option>)}
        </select>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by organization type">
          <option value="all">All types</option>
          {ORGANIZATION_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{organizationTypeLabel(type)}</option>)}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter by status">
          <option value="all">All readiness</option>
          <option value="needs_setup">Needs setup</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)} aria-label="Sort partners">
          <option value="name">Sort: Name</option>
          <option value="state">Sort: State</option>
          <option value="type">Sort: Type</option>
          <option value="readiness">Sort: Readiness</option>
        </select>
        {(search || stateFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all') && (
          <button
            type="button"
            className="partner-clear-filters"
            onClick={() => {
              setSearch('');
              setStateFilter('all');
              setTypeFilter('all');
              setStatusFilter('all');
            }}
          >
            Clear
          </button>
        )}
      </div>}

      {viewMode === 'seasons' ? (
        <OrganizationSeasonsView partners={partners} locations={locations} actorId={currentUser?.uid || null} />
      ) : loading ? (
        <p className="partner-list-empty">Loading partners…</p>
      ) : viewMode === 'rows' ? (
        <div className="partner-rows">
          <div className="partner-row partner-row-header" aria-hidden="true">
            <span />
            <span>Partner</span>
            <span>Organization type</span>
            <span>State</span>
            <span>Locations</span>
            <span>Readiness</span>
          </div>
          {filtered.map((partner) => {
            const partnerLocations = locationsByPartner.get(partner.id) || [];
            const states = Array.from(new Set(
              partnerLocations.map((location) => stateFromAddress(location.address)).filter(Boolean)
            ));
            const signal = signalForPartner(partner, contactCounts[partner.id]);
            const readiness = [Boolean(partner.organizationType), partnerLocations.length > 0, Boolean(contactCounts[partner.id])].filter(Boolean).length;
            return (
              <div
                key={partner.id}
                className="partner-row"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/app/admin/partners/${partner.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && event.target === event.currentTarget) {
                    navigate(`/app/admin/partners/${partner.id}`);
                  }
                }}
              >
                <span className={`partner-signal-dot partner-signal-dot--${signal.tone}`} aria-hidden="true" />
                <span className="partner-name-cell"><strong>{partner.name}</strong><StripePartnerBadge partner={partner} /></span>
                <select
                  className="partner-inline-select partner-row-secondary"
                  value={partner.organizationType || ''}
                  disabled={savingCell === `type:${partner.id}`}
                  aria-label={`Change type for ${partner.name}`}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => void handleTypeChange(partner, event.target.value)}
                >
                  <option value="" disabled>Unclassified</option>
                  {ORGANIZATION_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{organizationTypeLabel(type)}</option>
                  ))}
                </select>
                <div className="partner-state-editor partner-row-secondary" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className="partner-inline-button"
                    disabled={partnerLocations.length === 0}
                    onClick={() => setEditingStatesPartnerId((current) => current === partner.id ? null : partner.id)}
                  >
                    {states.length ? states.join(', ') : 'State unavailable'}
                  </button>
                  {editingStatesPartnerId === partner.id && (
                    <div className="partner-state-popover">
                      {partnerLocations.map((location) => (
                        <label key={location.id}>
                          <span>{location.name}</span>
                          <select
                            value={stateFromAddress(location.address) || ''}
                            disabled={savingCell === `state:${location.id}`}
                            onChange={(event) => void handleLocationStateChange(location, event.target.value)}
                          >
                            <option value="" disabled>Select state</option>
                            {STATE_OPTIONS.map((state) => <option key={state} value={state}>{state}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <span className="partner-row-secondary">{partnerLocations.length ? `${partnerLocations.length} ${partnerLocations.length === 1 ? 'location' : 'locations'}` : 'No locations'}</span>
                <span className={`partner-signal partner-signal--${readiness === 3 ? 'good' : 'warn'}`}>{readiness}/3 ready · {signal.label}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="partner-list-empty">No partners match these filters.</p>}
        </div>
      ) : (
        <div className="partner-card-grid">
          {filtered.map((partner) => {
            const partnerLocations = locationsByPartner.get(partner.id) || [];
            const states = Array.from(new Set(
              partnerLocations.map((location) => stateFromAddress(location.address)).filter(Boolean)
            ));
            const signal = signalForPartner(partner, contactCounts[partner.id]);
            return (
              <div
                key={partner.id}
                className="partner-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/app/admin/partners/${partner.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && event.target === event.currentTarget) {
                    navigate(`/app/admin/partners/${partner.id}`);
                  }
                }}
              >
                <span className="partner-card-heading">
                  <span className="partner-card-avatar">
                    {partner.branding?.logoUrl
                      ? <img src={partner.branding.logoUrl} alt="" />
                      : partner.name.slice(0, 1).toUpperCase()}
                  </span>
                  <span>
                    <span className="partner-card-name"><strong>{partner.name}</strong><StripePartnerBadge partner={partner} /></span>
                    <select
                      className="partner-inline-select"
                      value={partner.organizationType || ''}
                      disabled={savingCell === `type:${partner.id}`}
                      aria-label={`Change type for ${partner.name}`}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => void handleTypeChange(partner, event.target.value)}
                    >
                      <option value="" disabled>Unclassified</option>
                      {ORGANIZATION_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>{organizationTypeLabel(type)}</option>
                      ))}
                    </select>
                  </span>
                </span>
                <span className="partner-card-facts">
                  <span>{partnerLocations.length} {partnerLocations.length === 1 ? 'location' : 'locations'}</span>
                  <span className="partner-state-editor" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      className="partner-inline-button"
                      disabled={partnerLocations.length === 0}
                      onClick={() => setEditingStatesPartnerId((current) => current === partner.id ? null : partner.id)}
                    >
                      {states.length ? states.join(', ') : 'State unavailable'}
                    </button>
                    {editingStatesPartnerId === partner.id && (
                      <span className="partner-state-popover">
                        {partnerLocations.map((location) => (
                          <label key={location.id}>
                            <span>{location.name}</span>
                            <select
                              value={stateFromAddress(location.address) || ''}
                              disabled={savingCell === `state:${location.id}`}
                              onChange={(event) => void handleLocationStateChange(location, event.target.value)}
                            >
                              <option value="" disabled>Select state</option>
                              {STATE_OPTIONS.map((state) => <option key={state} value={state}>{state}</option>)}
                            </select>
                          </label>
                        ))}
                      </span>
                    )}
                  </span>
                </span>
                <span className={`partner-signal partner-signal--${signal.tone}`}>{signal.label}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="partner-list-empty">No partners match these filters.</p>}
        </div>
      )}
    </main>
  );
}
